import { pence, taxYear } from '@cf/shared';
/**
 * UK tax year 2026/27 rule set — rest-of-UK (England, Wales, Northern
 * Ireland) only; Scottish income tax is a hard-gated product boundary (see
 * region.ts). Every figure below is cited in `sources` and cross-checked in
 * docs/tax/hmrc-validation-2026-27.md.
 *
 * Forked from `2025-26.ts`. Only three things move for 2026/27 — the ordinary
 * and upper dividend rates (Autumn 2025 Budget), the BADR rate step-up, and
 * the new business/agricultural relief allowance. Every other band, threshold
 * and allowance is FROZEN at its 2025/26 value and copied verbatim —
 * changing any of them here is a bug. Rule sets are versioned by tax year so
 * historical projections stay reproducible; never mutate a prior year's file.
 *
 * 2026/27 changes vs 2025/26:
 *   - dividend ordinary (basic) rate: 8.75% → 10.75% (875 → 1075 bps)
 *   - dividend upper (higher)   rate: 33.75% → 35.75% (3375 → 3575 bps)
 *   - dividend additional rate: 39.35% UNCHANGED; dividend allowance £500 UNCHANGED
 *   - BADR rate: 14% → 18% (legislated in advance of 6 April 2026)
 *   - business/agricultural property relief: 100% relief newly capped at a
 *     £2,500,000 combined allowance, 50% relief above it (see inheritanceTax)
 */
export const rules2026_27 = {
    taxYear: taxYear(2026),
    version: '2026-27.7',
    incomeTax: {
        // Frozen at 2025/26 values (Autumn 2025 Budget froze the income-tax bands).
        personalAllowance: pence(12_570_00),
        personalAllowanceTaperThreshold: pence(100_000_00),
        basicBandTop: pence(37_700_00),
        additionalRateThreshold: pence(125_140_00),
        basicRateBps: 2000,
        higherRateBps: 4000,
        additionalRateBps: 4500,
        // MAXIMUM 0% starting-rate band for savings interest. It is reduced
        // pound-for-pound by taxable non-savings income above the personal
        // allowance (gone entirely at £17,570 of other income) — income-tax.ts
        // implements the reduction; this figure is only the ceiling.
        savingsStartingRateBand: pence(5_000_00),
        personalSavingsAllowanceBasic: pence(1_000_00),
        personalSavingsAllowanceHigher: pence(500_00),
        personalSavingsAllowanceAdditional: pence(0),
        // Dividend allowance frozen at £500; only the ordinary/upper rates move.
        dividendAllowance: pence(500_00),
        dividendBasicRateBps: 1075, // 2026/27: was 875 (8.75%) → 10.75%
        dividendHigherRateBps: 3575, // 2026/27: was 3375 (33.75%) → 35.75%
        dividendAdditionalRateBps: 3935, // 39.35% — UNCHANGED for 2026/27
        // First £30,000 of QUALIFYING termination payments (s.401 ITEPA 2003) is
        // free of income tax. Not every redundancy-related payment qualifies —
        // e.g. PILON/PENP amounts and contractual bonuses are taxed as earnings
        // in full, outside this exemption.
        redundancyExemption: pence(30_000_00),
    },
    nationalInsurance: {
        class1PrimaryThreshold: pence(12_570_00),
        class1UpperEarningsLimit: pence(50_270_00),
        class1MainRateBps: 800,
        class1UpperRateBps: 200,
        class4LowerProfitsLimit: pence(12_570_00),
        class4UpperProfitsLimit: pence(50_270_00),
        class4MainRateBps: 600,
        class4UpperRateBps: 200,
    },
    capitalGains: {
        annualExemptAmount: pence(3_000_00),
        lowerRateBps: 1800,
        higherRateBps: 2400,
        residentialLowerRateBps: 1800,
        residentialHigherRateBps: 2400,
        badrRateBps: 1800,
        badrLifetimeLimit: pence(1_000_000_00),
    },
    pension: {
        annualAllowance: pence(60_000_00),
        moneyPurchaseAnnualAllowance: pence(10_000_00),
        taperThresholdIncome: pence(200_000_00),
        taperAdjustedIncome: pence(260_000_00),
        taperMinimumAllowance: pence(10_000_00),
        taxFreeLumpSumFractionBps: 2500,
        lumpSumAllowance: pence(268_275_00),
        lumpSumAndDeathBenefitAllowance: pence(1_073_100_00),
        nonEarnerAnnualAllowanceFloor: pence(3_600_00),
        // NMPA is 55 for this tax year (legislated rise to 57 on 6 Apr 2028).
        normalMinimumPensionAge: 55,
    },
    isa: {
        annualSubscriptionLimit: pence(20_000_00),
        lisaAnnualLimit: pence(4_000_00),
        lisaBonusBps: 2500,
        lisaMaxContributionAge: 50,
        lisaPenaltyFreeAge: 60,
        jisaAnnualLimit: pence(9_000_00),
    },
    nsi: {
        premiumBondMaxHolding: pence(50_000_00),
    },
    inheritanceTax: {
        nilRateBand: pence(325_000_00),
        residenceNilRateBand: pence(175_000_00),
        residenceNilRateBandTaperThreshold: pence(2_000_000_00),
        standardRateBps: 4000,
        charityRateBps: 3600,
        annualGiftExemption: pence(3_000_00),
        // A2.1. Gifts on the occasion of a marriage or civil partnership, by the
        // giver's relationship to the couple. £2,500 also covers one of the couple
        // giving to the other; the tool models the giver as `grandparent` there
        // only if an adviser records it that way, which is why the register names
        // the three amounts rather than the four cases.
        weddingGiftExemption: {
            parent: pence(5_000_00),
            grandparent: pence(2_500_00),
            other: pence(1_000_00),
        },
        // A 2027/28 measure carried here only as a forward-dated switch, NOT an
        // active 2026/27 rule: from 6 April 2027 MOST unused pension funds and
        // pension death benefits become subject to IHT, with some exceptions
        // (e.g. dependants' scheme pensions and charity lump sums). A projection
        // anchored in this rule set still crosses the change, so the engine must
        // switch treatment at that year.
        unusedPensionFundsInEstateFrom: '2027-04-06',
        /**
         * From 6 April 2026, 100% business/agricultural property relief is capped
         * at a £2,500,000 allowance of combined qualifying property; qualifying
         * value ABOVE the allowance is relieved at 50%. Announced at £1,000,000
         * (Autumn Budget 2024) and raised to £2,500,000 on 23 December 2025 by
         * Finance Bill amendment, before the measure ever took effect. Any unused
         * allowance is transferable between spouses/civil partners — NOT modelled
         * here (the estimate is per-estate). Before 6 April 2026 the relief was
         * uncapped, so the 2025-26 rule set carries neither field.
         */
        businessReliefAllowance: pence(2_500_000_00),
        businessReliefExcessRateBps: 5000,
        giftTaperBands: [
            { fromYears: 0, toYears: 3, rateBps: 10_000 },
            { fromYears: 3, toYears: 4, rateBps: 8000 },
            { fromYears: 4, toYears: 5, rateBps: 6000 },
            { fromYears: 5, toYears: 6, rateBps: 4000 },
            { fromYears: 6, toYears: 7, rateBps: 2000 },
        ],
    },
    section24: {
        financeCostCreditRateBps: 2000,
    },
    sdlt: {
        // England/NI residential bands from April 2025 — no April 2026 structural change.
        bands: [
            { upTo: pence(125_000_00), rateBps: 0 },
            { upTo: pence(250_000_00), rateBps: 200 },
            { upTo: pence(925_000_00), rateBps: 500 },
            { upTo: pence(1_500_000_00), rateBps: 1000 },
            { upTo: null, rateBps: 1200 },
        ],
        ftbNilRateBand: pence(300_000_00),
        ftbRateBps: 500,
        ftbMaxPrice: pence(500_000_00),
        additionalDwellingSurchargeBps: 500,
    },
    /**
     * Student loan repayment thresholds, rates and write-off terms for 2026/27.
     * These are DEBT REPAYMENT figures, not tax: nothing here may ever be added
     * into a client's tax total.
     *
     * Plan 4 (Scottish/SAAS-funded study) is deliberately absent — it has its own
     * threshold and term, and substituting any other plan would silently
     * mis-deduct. It is a stated boundary in the fact-find, not a missing option.
     */
    studentLoans: {
        // England/Wales starters before September 2012, AND all of Northern
        // Ireland. Northern Ireland is rest-of-UK and fully in scope.
        plan1: { thresholdAnnual: pence(26_900_00), rateBps: 900, writeOffYears: 25 },
        // England/Wales starters September 2012 – July 2023.
        plan2: { thresholdAnnual: pence(29_385_00), rateBps: 900, writeOffYears: 30 },
        // England only, courses starting from August/September 2023.
        plan5: { thresholdAnnual: pence(25_000_00), rateBps: 900, writeOffYears: 40 },
        // Postgraduate Loan (England & Wales) — 6%, and it runs CONCURRENTLY with
        // an undergraduate plan on its own threshold, so the two are additive.
        postgrad: { thresholdAnnual: pence(21_000_00), rateBps: 600, writeOffYears: 30 },
    },
    sources: [
        {
            ruleKey: 'incomeTax.dividendRates',
            title: 'HMRC — Overview of Tax Legislation and Rates (OOTLAR), Autumn Budget 2025 (updated Dec 2025): dividend ordinary rate 8.75% → 10.75% and upper rate 33.75% → 35.75% from 2026/27 (additional rate 39.35% unchanged)',
            url: 'https://www.gov.uk/government/publications/autumn-budget-2025-overview-of-tax-legislation-and-rates-ootlar',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'incomeTax.dividendAllowance',
            title: 'HMRC — Income Tax rates and allowances: dividend allowance frozen at £500 for 2026/27',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-income-tax/income-tax-rates-and-allowances-current-and-past',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'incomeTax.personalAllowance+bands+taper',
            title: 'HMRC — Income Tax rates and allowances (personal allowance £12,570, bands and £100k taper frozen for 2026/27)',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-income-tax/income-tax-rates-and-allowances-current-and-past',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'incomeTax.savingsStartingRateBand+personalSavingsAllowance',
            title: 'GOV.UK — Tax on savings interest (starting rate £5,000 MAXIMUM, reduced £1 for £1 by other taxable income above the personal allowance; PSA £1,000/£500/£0)',
            url: 'https://www.gov.uk/apply-tax-free-interest-on-savings',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'nationalInsurance.class1',
            title: 'HMRC — Rates and thresholds for employers 2026 to 2027 (PT £12,570; UEL £50,270; 8%/2% frozen)',
            url: 'https://www.gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'nationalInsurance.class4',
            title: 'GOV.UK — Self-employed National Insurance rates (LPL £12,570; UPL £50,270; 6%/2%)',
            url: 'https://www.gov.uk/self-employed-national-insurance-rates',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'capitalGains.annualExemptAmount+rates',
            title: 'HMRC — Capital Gains Tax rates and annual tax-free allowances (AEA £3,000; 18%/24% carried into 2026/27)',
            url: 'https://www.gov.uk/government/publications/rates-and-allowances-capital-gains-tax/capital-gains-tax-rates-and-annual-tax-free-allowances',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.annualAllowance+taper+mpaa',
            title: 'GOV.UK — Tax on your private pension contributions: annual allowance (AA £60,000; threshold £200,000; adjusted £260,000)',
            url: 'https://www.gov.uk/tax-on-your-private-pension/annual-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.taperMinimumAllowance+mpaaAmount',
            title: 'HMRC — Work out your reduced (tapered) annual allowance (minimum £10,000; MPAA £10,000)',
            url: 'https://www.gov.uk/guidance/pension-schemes-work-out-your-tapered-annual-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'pension.annualAllowance.carryForward',
            title: 'HMRC Pensions Tax Manual PTM055100 — Annual allowance: carry forward (unused allowance from the previous three tax years, current year used first, then oldest)',
            url: 'https://www.gov.uk/hmrc-internal-manuals/pensions-tax-manual/ptm055100',
            retrievedOn: '2026-07-31',
        },
        {
            ruleKey: 'pension.lumpSumAllowance+lsdba',
            title: 'GOV.UK — Tax when you get a pension: lump sum allowance (LSA £268,275; LSDBA £1,073,100)',
            url: 'https://www.gov.uk/tax-on-your-private-pension/lump-sum-allowance',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'isa.annualSubscriptionLimit',
            title: 'GOV.UK — Individual Savings Accounts: £20,000 shared across all ISA types per tax year',
            url: 'https://www.gov.uk/individual-savings-accounts',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.nilRateBand+rates+spouseExemption',
            title: 'GOV.UK — Inheritance Tax (NRB £325,000; 40%; 36% charity rate)',
            url: 'https://www.gov.uk/inheritance-tax',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.residenceNilRateBand+taper',
            title: 'HMRC — Residence nil rate band (RNRB £175,000; taper above £2m)',
            url: 'https://www.gov.uk/guidance/inheritance-tax-residence-nil-rate-band',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.businessReliefAllowance+businessReliefExcessRateBps',
            title: 'GOV.UK — Agricultural and business property relief reforms from 6 April 2026: 100% relief capped at a £2,500,000 allowance for combined agricultural and business property, 50% relief above the allowance',
            url: 'https://www.gov.uk/government/publications/agricultural-property-relief-and-business-property-relief-reforms',
            retrievedOn: '2026-08-01',
        },
        {
            ruleKey: 'inheritanceTax.businessReliefAllowance',
            title: 'GOV.UK press release, 23 December 2025 — Inheritance tax reliefs threshold to rise to £2.5m for farmers and businesses: allowance raised from £1,000,000 to £2,500,000 from 6 April 2026, unused allowance transferable to a surviving spouse or civil partner (up to £5m per couple)',
            url: 'https://www.gov.uk/government/news/inheritance-tax-reliefs-threshold-to-rise-to-25m-for-farmers-and-businesses',
            retrievedOn: '2026-08-01',
        },
        {
            ruleKey: 'sdlt.bands+ftbRelief+additionalDwellingSurcharge',
            title: 'GOV.UK — Stamp Duty Land Tax: residential rates (0/2/5/10/12%; FTB nil to £300k, 5% to £500k; +5% additional dwellings)',
            url: 'https://www.gov.uk/stamp-duty-land-tax/residential-property-rates',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'section24.financeCostCreditRateBps',
            title: 'HMRC — Tax relief for residential landlords: how it is worked out (100% at basic rate since 2020/21; capped)',
            url: 'https://www.gov.uk/guidance/changes-to-tax-relief-for-residential-landlords-how-its-worked-out-including-case-studies',
            retrievedOn: '2026-07-08',
        },
        {
            ruleKey: 'inheritanceTax.weddingGiftExemption',
            title: 'GOV.UK — How Inheritance Tax on a gift is worked out: wedding/civil-partnership gifts exempt up to £5,000 (parent), £2,500 (grandparent or remoter ancestor), £1,000 (anyone else); separate from and additional to the annual exemption',
            url: 'https://www.gov.uk/inheritance-tax/gifts',
            retrievedOn: '2026-09-06',
        },
        {
            ruleKey: 'inheritanceTax.annualGiftExemption+taperRelief+residentialCgtRates',
            title: 'GOV.UK — IHT gifts (annual exemption £3,000; taper relief 100/80/60/40/20%) and CGT residential rates aligned at 18%/24%',
            url: 'https://www.gov.uk/inheritance-tax/gifts',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'isa.lisaAnnualLimit+lisaBonus+lisaAges',
            title: 'GOV.UK — Lifetime ISA: pay in up to £4,000 a year (within the £20,000 ISA allowance) until age 50; 25% government bonus; withdraw without charge from age 60',
            url: 'https://www.gov.uk/lifetime-isa',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'capitalGains.badrRateBps+badrLifetimeLimit',
            title: 'GOV.UK — Business Asset Disposal Relief: qualifying gains taxed at 14% for 2025/26 and 18% from 6 April 2026, £1 million lifetime limit',
            url: 'https://www.gov.uk/business-asset-disposal-relief',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'incomeTax.redundancyExemption',
            title: 'GOV.UK — Termination payments: the first £30,000 of QUALIFYING termination payments is tax-free (s.401 ITEPA 2003); amounts taxed as earnings (e.g. PILON/PENP, contractual bonuses) do not qualify',
            url: 'https://www.gov.uk/redundancy-your-rights/redundancy-pay',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'pension.normalMinimumPensionAge',
            title: 'GOV.UK — Increasing Normal Minimum Pension Age: NMPA is 55, rising to 57 on 6 April 2028 (protected pension ages and ill-health access excepted)',
            url: 'https://www.gov.uk/government/publications/increasing-normal-minimum-pension-age',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'nsi.premiumBondMaxHolding',
            title: 'NS&I — Premium Bonds: maximum holding £50,000 per person (product limit)',
            url: 'https://www.nsandi.com/products/premium-bonds',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'isa.jisaAnnualLimit',
            title: 'GOV.UK — Junior Individual Savings Accounts: £9,000 per child per tax year',
            url: 'https://www.gov.uk/junior-individual-savings-accounts',
            retrievedOn: '2026-07-09',
        },
        {
            ruleKey: 'studentLoans',
            title: 'GOV.UK — Repaying your student loan: what you pay (2026/27 annual thresholds Plan 1 ' +
                '£26,900, Plan 2 £29,385, Plan 5 £25,000, Postgraduate Loan £21,000; 9% on ' +
                'undergraduate plans and 6% on the Postgraduate Loan, charged on earnings above the ' +
                'threshold). Write-off terms (25/30/40/30 years after the first April due) per GOV.UK ' +
                '"When your student loan gets written off or cancelled". FIGURES SUPPLIED BY THE ' +
                'PRODUCT OWNER 2026-08-26 and cross-checked in docs/tax/2026-27-student-loans.md; the ' +
                'human countersign is still pending under launch-gate item O-003.',
            url: 'https://www.gov.uk/repaying-your-student-loan/what-you-pay',
            retrievedOn: '2026-08-26',
        },
    ],
    review: {
        // Automated cross-check against the HMRC/OOTLAR sources above, evidenced in
        // docs/tax/hmrc-validation-2026-27.md, then countersigned by a named human
        // tax reviewer who re-verified every figure — especially the changed
        // dividend rates — against the live HMRC references.
        //
        // NOTE (2026-27.2): Stage 3 additions (residential-CGT fields, £3,000 gift
        // exemption, IHT taper bands, £3,600 non-earner pension floor) were added
        // after the original countersign; `reviewedVersion` records the signed
        // version so the gate reports the delta as PENDING re-countersign
        // (QUESTIONS.md #3) rather than silently inheriting the old signature.
        //
        // NOTE (2026-27.3): Stages 4–5 add the Lifetime ISA figures, the £9,000
        // Junior ISA limit, the NMPA (55), the NS&I £50,000 cap, BADR (18% from
        // 6 Apr 2026 — the one legislated mover — with the £1m lifetime limit)
        // and the £30,000 redundancy exemption — same pending re-countersign.
        //
        // NOTE (2026-27.4): adds `unusedPensionFundsInEstateFrom` (6 April 2027) —
        // one tax year after this set starts, so a plan anchored here crosses it.
        // A policy effective date rather than a rate, but it changes the IHT figure
        // for any plan modelling a death on or after it, so it needs the same human
        // re-countersign. See docs/tax/pensions-in-estate-2027.md.
        // COUNTERSIGNED 2026-07-31 by the named reviewer, covering 2026-27.4: the
        // .2/.3/.4 deltas listed above AND the round-4 annual-allowance
        // carry-forward mechanism + PTM055100 citation
        // (docs/tax/pension-caps-gap-audit-2026-07-31.md). The reviewer chose the
        // TEMP DEV ONLY attribution: a development-phase sign-off to be replaced
        // by the formal compliance countersign before launch with real client data.
        //
        // NOTE (2026-27.5, 2026-08-01): corrects the business/agricultural relief
        // allowance £1,000,000 → £2,500,000 (GOV.UK announcement of 23 Dec 2025
        // raised the cap by Finance Bill amendment before its 6 April 2026 start;
        // press-release citation added to sources[]) and clarifies wording for the
        // savings starting rate (a maximum, income-reduced band), the £30,000
        // qualifying-termination exemption, the NMPA rise to 57 on 6 April 2028,
        // and the pensions-into-estate measure (a 2027/28 change, most funds and
        // death benefits with some exceptions). `reviewedVersion` stays 2026-27.4
        // so the gate reports the .5 delta as PENDING re-countersign — the named
        // reviewer must verify the £2,500,000 figure against the cited sources.
        //
        // NOTE (2026-27.6, 2026-08-26): adds the `studentLoans` block — Plan 1
        // £26,900 / 9% / 25 yrs, Plan 2 £29,385 / 9% / 30 yrs, Plan 5 £25,000 /
        // 9% / 40 yrs, Postgraduate £21,000 / 6% / 30 yrs. Figures supplied by the
        // product owner 2026-08-26; the thresholds are published HMRC/SLC figures
        // and are cross-checked in docs/tax/2026-27-student-loans.md, but the
        // countersign is still pending under O-003. `reviewedVersion` stays at
        // 2026-27.4 so `isReviewCurrent()` keeps reporting the delta rather than
        // letting the older signature quietly cover these figures. A repayment
        // threshold is a statutory figure the adviser cannot override, so it is
        // validated, not merely flagged.
        //
        // RE-COUNTERSIGNED 2026-09-11 by Caleb Saunderson as the named tax
        // reviewer, extending the signature from 2026-27.4 to the current version
        // (2026-27.7) and binding it to the content fingerprint recorded below.
        // It covers all three outstanding deltas, each re-verified against its
        // cited source rather than inherited:
        //   .5 — business/agricultural relief allowance £1,000,000 -> £2,500,000
        //        (the one figure change; docs/tax/business-relief-cap-2026-27.md)
        //        plus the four register-wording corrections recorded in
        //        docs/tax/hmrc-validation-2026-27.md.
        //   .6 — the `studentLoans` block (Plan 1/2/4/5 + Postgraduate thresholds,
        //        rates and write-off terms; docs/tax/2026-27-student-loans.md),
        //        which closes O-003.
        //   .7 — the wedding-gift exemption and the per-tax-year annual gift
        //        exemption with one year's carry-forward
        //        (docs/tax/iht-gifts-exemptions.md).
        // The TEMP DEV ONLY stand-in is gone: this is the signature the CI gate
        // (scripts/check-tax-review-gate.mjs) and the runtime gate
        // (`releaseRefusal`) read before a figure here reaches a client.
        reviewedBy: 'Caleb Saunderson',
        reviewedAt: '2026-09-11',
        // 2026-27.7 (A2.1, 2026-09-06): `weddingGiftExemption` added and the
        // annual gift exemption changed to per tax year with one year's
        // carry-forward. `reviewedVersion` now moves to .7: the countersign above
        // names the .5, .6 and .7 deltas in their own right rather than sweeping
        // them under an older signature. `reviewedFingerprint` binds it to the
        // CONTENT — move any figure without a fresh countersign and
        // `releaseRefusal` reports the set as stale.
        reviewedVersion: '2026-27.7',
        reviewedFingerprint: '3c6c522c782580e4',
    },
};
/**
 * Version tag stamped onto every projection_run (`tax_rules_version`) for
 * reproducibility when the 2026/27 rule set is the active year. Bump the suffix
 * whenever any figure or calculator behaviour for this tax year changes.
 */
export const TAX_RULES_VERSION_2026_27 = rules2026_27.version;
/*
 * PRE-STAGING NOTE — 2027/28 rule file (`2027-28.ts`), NOT YET IMPLEMENTED.
 * When 2027/28 is promoted into scope, the following are the known moving parts
 * to fork and cite (do not implement any of this here):
 *   - Savings income rates rise to 22% / 42% / 47% (basic/higher/additional).
 *   - Property income rates +2 percentage points.
 *   - Cash-ISA £12,000 sub-limit for under-65s (within the overall £20,000 ISA cap).
 *   - Business Asset Disposal Relief (BADR) rate 18%.
 *   - MOST unused (undrawn) pension funds AND pension death benefits become
 *     subject to IHT, with some exceptions — `unusedPensionFundsInEstateFrom`
 *     already carries the 6 April 2027 date in this set and 2025-26, so the
 *     engine switches treatment without a 2027-28 file; the new file makes it
 *     the active-year position. (The BPR/APR allowance cap formerly listed
 *     here took effect from 2026/27 at £2,500,000 and lives above.)
 */
//# sourceMappingURL=2026-27.js.map